import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable, tap } from 'rxjs';
import { OrdersApiService } from '../../../services/api/orders-api.service';
import { OrderAoeQuestionsDto } from '../models/dtos/order-aoe-questions-dto';
import { AoeQuestion } from '../models/aoe-question.model';
import { AoeQuestionAnswerDto } from '../models/dtos/aoe-question-answer-dto';
import { AoeTestSpecificQuestion } from '../models/aoe-test-specific-question.model';
import { OptionDto } from '@bioreferencehealth/ui-kit';
import { FetchAoeQuestions, FetchAoeQuestionsIfEmpty } from './actions/aoe-questions.actions';
import { DefaultOrderCreated, FetchOrderSuccess } from './actions/order.actions';
import { OrderType } from 'src/app/enums/order-type.enum';

interface AoeQuestionsStateModel {
  status: 'notLoaded' | 'loading' | 'loaded';
  orderId?: number;
  commonQuestions?: OrderAoeQuestionsDto['commonQuestions'];
  testSpecificQuestions?: OrderAoeQuestionsDto['testSpecificQuestions'];
}

@State<AoeQuestionsStateModel>({
  name: 'aoeQuestions',
  defaults: {
    status: 'notLoaded'
  }
})
@Injectable()
export class AoeQuestionsState {
  private readonly api: OrdersApiService = inject(OrdersApiService);

  @Selector([AoeQuestionsState])
  public static commonQuestions(state: AoeQuestionsStateModel): AoeQuestion[] | undefined {
    return state.commonQuestions?.map(value => {
      return {
        ...value,
        answers: this.convertAnswersToOptionDtos(value.answers)
      };
    });
  }

  @Selector([AoeQuestionsState])
  public static testSpecificQuestions(state: AoeQuestionsStateModel): AoeTestSpecificQuestion[] | undefined {
    return state.testSpecificQuestions?.map(value => {
      return {
        ...value,
        questions: value.questions.map(question => {
          return {
            ...question,
            answers: this.convertAnswersToOptionDtos(question.answers)
          };
        })
      };
    });
  }

  @Selector([AoeQuestionsState])
  public static loading(state: AoeQuestionsStateModel): boolean {
    return state.status === 'loading';
  }

  @Selector([AoeQuestionsState])
  public static loaded(state: AoeQuestionsStateModel): boolean {
    return state.status === 'loaded';
  }

  @Selector([AoeQuestionsState.testSpecificQuestions])
  public static groupedTestSpecificQuestions(
    testSpecificQuestions: AoeTestSpecificQuestion[] | undefined
  ): AoeTestSpecificQuestion[][] | undefined {
    if (!testSpecificQuestions) {
      return undefined;
    }

    const testSpecificMap = new Map();
    testSpecificQuestions.forEach(testSpecificQuestion => {
      if (!testSpecificMap.has(testSpecificQuestion.testCode)) {
        testSpecificMap.set(testSpecificQuestion.testCode, []);
      }

      testSpecificMap.get(testSpecificQuestion.testCode).push(testSpecificQuestion);
    });
    return [...testSpecificMap.values()];
  }

  @Action([DefaultOrderCreated, FetchOrderSuccess])
  public defaultOrderCreatedSuccess(ctx: StateContext<AoeQuestionsStateModel>): void {
    ctx.setState({ commonQuestions: undefined, testSpecificQuestions: undefined, status: 'notLoaded' });
  }

  @Action([FetchAoeQuestions], { cancelUncompleted: true })
  public fetchQuestions(
    ctx: StateContext<AoeQuestionsStateModel>,
    { orderId, orderType }: FetchAoeQuestions
  ): Observable<OrderAoeQuestionsDto> {
    if (orderType === OrderType.DrawLater) {
      ctx.setState({ orderId, status: 'loaded' });
      return EMPTY;
    }

    ctx.setState({ status: 'loading' });

    return this.api.getAoeQuestions(orderId).pipe(tap(result => ctx.setState({ orderId, ...result, status: 'loaded' })));
  }

  @Action([FetchAoeQuestionsIfEmpty], { cancelUncompleted: true })
  public fetchWhenEmptyQuestions(
    ctx: StateContext<AoeQuestionsStateModel>,
    { orderId, orderType }: FetchAoeQuestionsIfEmpty
  ): Observable<OrderAoeQuestionsDto> {
    const state = ctx.getState();
    const status = state.status;
    const currentOrderId = state.orderId;

    if (status === 'loaded' && orderId === currentOrderId) {
      return EMPTY;
    }

    if (orderType === OrderType.DrawLater) {
      ctx.setState({ orderId, status: 'loaded' });
      return EMPTY;
    }

    ctx.setState({ status: 'loading' });

    return this.api.getAoeQuestions(orderId).pipe(tap(result => ctx.setState({ orderId, ...result, status: 'loaded' })));
  }

  private static convertAnswersToOptionDtos(answers: AoeQuestionAnswerDto[]): OptionDto<AoeQuestionAnswerDto>[] {
    return answers.map((answer): OptionDto<AoeQuestionAnswerDto> => {
      return {
        id: answer.id,
        value: answer.text,
        payload: answer
      };
    });
  }
}
